import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Button,
  CircularProgress,
  makeStyles,
  Chip,
} from '@material-ui/core';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { InfoCard, Progress } from '@backstage/core-components';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  projectItem: {
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
  successMessage: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.success.main,
    marginTop: theme.spacing(2),
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

interface NotificationPreferences {
  accessibleProjects: string[];
  enabledProjects: string[];
  hasCustomPreferences: boolean;
}

export const NotificationSettings: React.FC = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [username, setUsername] = useState('');
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    accessibleProjects: [],
    enabledProjects: [],
    hasCustomPreferences: false,
  });
  const [enabledSet, setEnabledSet] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchPreferences = async () => {
      setLoading(true);
      try {
        const profile = await identityApi.getProfileInfo();
        const email = profile.email || '';
        setUsername(email);

        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();

        const response = await fetch(
          `${baseUrl}/notifications/preferences?username=${encodeURIComponent(email)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setPreferences(data);
          setEnabledSet(new Set(data.enabledProjects));
        }
      } catch (error) {
        console.error('Failed to fetch notification preferences:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [identityApi, discoveryApi]);

  const handleToggle = (project: string) => {
    const newEnabledSet = new Set(enabledSet);
    if (newEnabledSet.has(project)) {
      newEnabledSet.delete(project);
    } else {
      newEnabledSet.add(project);
    }
    setEnabledSet(newEnabledSet);
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();

      const response = await fetch(`${baseUrl}/notifications/preferences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          enabledProjects: Array.from(enabledSet),
        }),
      });

      if (response.ok) {
        setSaved(true);
        setPreferences({
          ...preferences,
          enabledProjects: Array.from(enabledSet),
          hasCustomPreferences: true,
        });
        setTimeout(() => setSaved(false), 3000);
      } else {
        const error = await response.json();
        console.error('Failed to save preferences:', error);
        alert(`Failed to save preferences: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <InfoCard title="Notification Preferences">
        <Progress />
      </InfoCard>
    );
  }

  const hasChanges = 
    enabledSet.size !== preferences.enabledProjects.length ||
    !preferences.enabledProjects.every(p => enabledSet.has(p));

  return (
    <InfoCard title="Notification Preferences">
      <Box>
        <Typography variant="body1" paragraph>
          Control which project notifications you want to receive. By default, you receive
          notifications for all projects you have access to.
        </Typography>

        {preferences.accessibleProjects.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            You don't have access to any projects yet.
          </Typography>
        ) : (
          <>
            <Box className={classes.section}>
              <Typography variant="h6" gutterBottom>
                Projects ({enabledSet.size} of {preferences.accessibleProjects.length} enabled)
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Select the projects you want to receive notifications from:
              </Typography>

              <FormGroup>
                {preferences.accessibleProjects.map(project => (
                  <FormControlLabel
                    key={project}
                    className={classes.projectItem}
                    control={
                      <Checkbox
                        checked={enabledSet.has(project)}
                        onChange={() => handleToggle(project)}
                        color="primary"
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center">
                        <Typography variant="body1">{project}</Typography>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </Box>

            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className={classes.saveButton}
              >
                {saving ? <CircularProgress size={20} /> : 'Save Preferences'}
              </Button>

              {saved && (
                <Box className={classes.successMessage}>
                  <CheckCircleIcon style={{ marginRight: 8 }} />
                  <Typography variant="body2">Preferences saved successfully!</Typography>
                </Box>
              )}
            </Box>

            {!preferences.hasCustomPreferences && (
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  💡 <strong>Tip:</strong> All your accessible projects are enabled by default.
                  You can disable specific projects if you don't want their notifications.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </InfoCard>
  );
};

